import { Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  X,
  RotateCcw,
  AlertTriangle,
  Info,
  Building2,
  Calendar,
  TrendingUp,
  BarChart3,
  Briefcase,
} from 'lucide-react'
import { AssetWithClassification, getAssetClassTheme, getAssetClassLabel, isNewAsset } from '@/types/assets'
import { ClassificationToggle } from './ClassificationToggle'
import { AssetClass } from '@/types/api'
import { cn } from '@/lib/utils'

interface AssetDetailPanelProps {
  /** Asset to display (null if closed) */
  asset: AssetWithClassification | null
  /** Callback when panel closes */
  onClose: () => void
  /** Callback when classification changes */
  onClassificationChange: (
    assetId: string,
    classification: AssetClass,
    isOverride: boolean
  ) => void
  /** Callback to reset classification to default */
  onResetToDefault: (assetId: string) => void
}

/**
 * AssetDetailPanel Component
 *
 * A slide-out panel showing comprehensive asset details including:
 * - Asset header with ticker, name, and classification
 * - Classification controls with reset capability
 * - Metadata (sector, market cap, listing date)
 * - Portfolio impact (positions, risk exposure)
 * - Proxy info (for assets using synthetic data)
 *
 * @example
 * ```tsx
 * <AssetDetailPanel
 *   asset={selectedAsset}
 *   onClose={() => setSelectedAsset(null)}
 *   onClassificationChange={(id, val, override) => console.log(id, val, override)}
 *   onResetToDefault={(id) => console.log('reset', id)}
 * />
 * ```
 */
export function AssetDetailPanel({
  asset,
  onClose,
  onClassificationChange,
  onResetToDefault,
}: AssetDetailPanelProps) {
  const isOpen = asset !== null

  if (!asset) {
    return null
  }

  const theme = getAssetClassTheme(asset.assetClass)
  const isNew = isNewAsset(asset.metadata?.listingDate || asset.listingDate)
  const hasProxy = asset.proxyInfo !== undefined
  const hasPositions = (asset.positionCount || 0) > 0

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="space-y-4">
          {/* Asset Header */}
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-2xl font-bold">
                  {asset.symbol}
                </span>
                <Badge className={cn('font-medium', theme.badge)}>
                  {getAssetClassLabel(asset.assetClass)}
                </Badge>
                {isNew && (
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200"
                  >
                    New
                  </Badge>
                )}
              </div>
              <SheetTitle className="text-lg font-medium text-gray-700">
                {asset.name}
              </SheetTitle>
            </div>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>

          {/* Override Warning */}
          {asset.isOverride && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-800">
                    Classification Overridden
                  </p>
                  <p className="text-xs text-amber-700">
                    Rules-based default: {getAssetClassLabel(asset.rulesBasedClass)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Classification Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Classification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current</span>
                <ClassificationToggle
                  value={asset.assetClass}
                  isOverride={asset.isOverride}
                  rulesBasedClass={asset.rulesBasedClass}
                  onChange={(value, isOverride) =>
                    onClassificationChange(asset.id, value, isOverride)
                  }
                  onReset={() => onResetToDefault(asset.id)}
                  hasPositions={hasPositions}
                  size="md"
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Rules-Based Default</span>
                <Badge variant="secondary" className={getAssetClassTheme(asset.rulesBasedClass).badge}>
                  {getAssetClassLabel(asset.rulesBasedClass)}
                </Badge>
              </div>

              {asset.isOverride && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => onResetToDefault(asset.id)}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset to Default
                </Button>
              )}

              {hasPositions && (
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      This asset has {asset.positionCount} position
                      {asset.positionCount !== 1 ? 's' : ''} in your portfolio.
                      Classification changes will affect scenario calculations.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metadata Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Asset Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3">
                {asset.metadata?.sector && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600 flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      Sector
                    </dt>
                    <dd className="text-sm font-medium">{asset.metadata.sector}</dd>
                  </div>
                )}

                {asset.metadata?.marketCap && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Market Cap
                    </dt>
                    <dd className="text-sm font-medium">{asset.metadata.marketCap}</dd>
                  </div>
                )}

                {(asset.metadata?.listingDate || asset.listingDate) && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Listed
                    </dt>
                    <dd className="text-sm font-medium">
                      {new Date(asset.metadata?.listingDate || asset.listingDate || '').toLocaleDateString()}
                      {isNew && (
                        <span className="ml-2 text-xs text-blue-600">(New)</span>
                      )}
                    </dd>
                  </div>
                )}

                {asset.metadata?.description && (
                  <>
                    <Separator className="my-3" />
                    <div className="space-y-1">
                      <dt className="text-sm text-gray-600">Description</dt>
                      <dd className="text-sm text-gray-700 leading-relaxed">
                        {asset.metadata.description}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Portfolio Impact Section */}
          {hasPositions && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Portfolio Impact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Position Count</span>
                    <Badge variant="secondary">{asset.positionCount}</Badge>
                  </div>

                  <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Note:</strong> Changing this asset&apos;s classification
                      will affect scenario calculations, including P&L waterfalls,
                      risk decomposition, and drawdown paths.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Proxy Info Section */}
          {hasProxy && asset.proxyInfo && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Proxy Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TooltipProvider>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Proxy Asset</span>
                      <Badge variant="outline" className="font-mono">
                        {asset.proxyInfo.proxyTicker}
                      </Badge>
                    </div>

                    <div className="flex justify-between">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm text-gray-600 underline decoration-dotted cursor-help">
                            Overlap Beta
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">
                            Beta coefficient calculated from overlapping price history
                            between this asset and its proxy.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <span className="text-sm font-medium">
                        {asset.proxyInfo.overlapBeta.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Overlap Period</span>
                      <span className="text-sm text-gray-700">
                        {asset.proxyInfo.overlapPeriod}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm text-gray-600 underline decoration-dotted cursor-help">
                            Synthetic %
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">
                            Percentage of historical data generated synthetically
                            from proxy returns vs actual market data.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <span className="text-sm font-medium">
                        {asset.proxyInfo.percentSynthetic.toFixed(1)}%
                      </span>
                    </div>

                    <div className="rounded-md bg-gray-50 border border-gray-200 p-2 mt-3">
                      <p className="text-xs text-gray-600">
                        This asset uses synthetic historical data blended from{' '}
                        <strong>{asset.proxyInfo.proxyTicker}</strong> to extend
                        price history for stress testing calculations.
                      </p>
                    </div>
                  </div>
                </TooltipProvider>
              </CardContent>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
